import { logger } from "@/utils/logger";
import store from "@/store";
import { getUser } from "@/store/services/auth";

/** Security group resourceUid from a picklist string or API userGroup object. */
export function getGroupSecurityUid(item: any): string | null {
  if (item == null) return null;
  if (typeof item === "string") {
    const trimmed = item.trim();
    return trimmed || null;
  }
  const uid = item?.group?.resourceUid;
  if (uid != null && String(uid).trim() !== "") {
    return String(uid);
  }
  return null;
}

export function countValidUserGroups(groups: any[] | undefined): number {
  if (!Array.isArray(groups)) return 0;
  return groups.filter((g) => getGroupSecurityUid(g) != null).length;
}

export function hasValidUserGroups(groups: any[] | undefined): boolean {
  return countValidUserGroups(groups) > 0;
}

export function buildUserGroupsFromSelection(selectedUids: any[]): any[] {
  if (!Array.isArray(selectedUids)) return [];

  const savedGroups = selectedUids.filter(
    (item: any) =>
      getGroupSecurityUid(item) != null ||
      (item?.resourceUid != null && String(item.resourceUid).trim() !== "")
  );

  const uniqueUids = new Set(
    savedGroups
      .map((item) => getGroupSecurityUid(item))
      .filter((uid): uid is string => uid != null)
  );

  return Array.from(uniqueUids).map((uid) => {
    const resource = selectedUids.find((resourceUid: any) => {
      if (typeof resourceUid === "string") return resourceUid === uid;
      return resourceUid?.group?.resourceUid === uid;
    });

    return {
      businessData: null,
      daaq: "/",
      group: {
        objectType: "odm.user.securitygroup",
        resourceUid: uid,
        systemUid: "odm-user",
      },
      objectType: "odm.user.usergroup",
      resourceUid:
        resource && resource.resourceUid ? resource.resourceUid : "",
      systemUid: "odm-user",
    };
  });
}

/**
 * Prevents accidental wipe: if previous had valid groups and next has none, keep previous.
 */
export function safeApplyUserGroups(previous: any[], next: any[]): any[] {
  const prevCount = countValidUserGroups(previous);
  const nextCount = countValidUserGroups(next);

  if (prevCount > 0 && nextCount === 0) {
    logger.warn(
      "[userGroups] prevented overwrite with empty list",
      { previous, next }
    );
    return previous;
  }
  return next;
}

/** Reload userGroups from API when store looks empty for an existing user. */
export async function ensureUserGroupsFromApi(
  userResourceUid: string | undefined
): Promise<any[]> {
  const selected = store.state.userModule.selectedUserDetails;
  if (!selected) return [];

  let groups = selected.userGroups ?? [];
  if (hasValidUserGroups(groups)) {
    return groups;
  }

  if (!userResourceUid) {
    return groups;
  }

  try {
    const response = await getUser(userResourceUid);
    const apiGroups = response?.data?.userGroups;
    if (hasValidUserGroups(apiGroups)) {
      selected.userGroups = apiGroups;
      return apiGroups;
    }
  } catch (e) {
    logger.warn("[userGroups] ensureUserGroupsFromApi failed", e);
  }

  return groups;
}

export function applyUserGroupsToStore(selectedUids: any): void {
  const previous =
    store.state.userModule.selectedUserDetails?.userGroups ?? [];
  const next = buildUserGroupsFromSelection(selectedUids);
  store.state.userModule.selectedUserDetails.userGroups = safeApplyUserGroups(
    previous,
    next
  );
}
